import {
  useGetApPayeesData,
  useGetDeptsData,
  useGetOfficeInvoiceReportSettings,
  useGetOfficeMembersData,
} from '@/context/services/approving_invoice_reports/ApprovingInvoiceReports.service';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { useTranslation } from '@/i18n';
import { useEffect, useMemo, useRef, useState } from 'react';

export const useConfigForm = () => {
  const { t } = useTranslation();
  const { data: officeInvoiceReportSettings } =
    useGetOfficeInvoiceReportSettings();
  const instantPayeeSetting =
    officeInvoiceReportSettings?.data?.instant_payee_setting;
  const reportDeptsSetting =
    officeInvoiceReportSettings?.data?.report_depts_setting;
  const deptsExisted = officeInvoiceReportSettings?.data?.depts_existed;

  // Custom validation rules for the search field
  const checkLengthValidationRules = useMemo(
    () => ({
      maxLength: {
        value: 100,
        message: t('search.validation.max_length', { maxLength: 100 }),
      },
    }),
    [t]
  );

  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const sizeSmall = 'small' as const;

  const customProps = useMemo(
    () => ({
      labelProps: {
        number: { text: t('report_number') },
        createdDate: {
          text: t('approving_invoice_reports.table.columns.created_date'),
        },
        submittedDate: {
          text: t('approving_invoice_reports.table.columns.application_date'),
        },
        dueDate: { text: t('payment_month_label') },
        bookDate: { text: t('payment_closing_date_label') },
        approvedDate: { text: t('approval_date_label') },
        withoutAgent: { text: t('exclude_proxy_approval') },
      },
      selectProps: {
        config: { maxTagCount: 1, placeholder: '', size: sizeSmall },
        configApplicantDepartment: {
          maxTagCount: 1,
          placeholder: '',
          inputPlaceholder: t(
            'approving_invoice_reports.search.dept.placeholder'
          ),
          size: sizeSmall,
        },
        configDept: {
          maxTagCount: 1,
          disabled: !reportDeptsSetting?.enabled,
          placeholder: '',
          inputPlaceholder: t(
            'approving_invoice_reports.search.dept.placeholder'
          ),
          size: sizeSmall,
        },
        configApPayee: {
          inputPlaceholder: t(
            'approving_invoice_reports.search.ap_payee.placeholder'
          ),
          size: sizeSmall,
        },
      },
      inputProps: {
        query: {
          search: true,
          size: sizeSmall,
        },
        number: {
          size: sizeSmall,
          search: false,
        },
      },
    }),
    [t, reportDeptsSetting]
  );

  const defaultOptions = useMemo(() => {
    // loop name and value
    const apPayeeDefaultOptionsData = instantPayeeSetting?.enabled
      ? instantPayeeSetting?.instant_payee_search_option
      : undefined;
    const deptDefaultOptionsData = reportDeptsSetting?.enabled
      ? reportDeptsSetting?.blank_report_dept_option
      : undefined;
    const apPayeeDefaultOptions = apPayeeDefaultOptionsData
      ? [
          {
            label: apPayeeDefaultOptionsData?.label || '',
            value: apPayeeDefaultOptionsData?.id || '',
          },
        ]
      : undefined;
    const deptDefaultOptions = deptDefaultOptionsData
      ? [
          {
            label: deptDefaultOptionsData?.label || '',
            value: deptDefaultOptionsData?.id || '',
          },
        ]
      : undefined;
    return {
      apPayeeDefaultOptions,
      deptDefaultOptions,
    };
  }, [instantPayeeSetting, reportDeptsSetting]);
  return {
    customProps,
    defaultOptions,
    instantPayeeSetting,
    reportDeptsSetting,
    deptsExisted,
    checkLengthValidationRules,
  };
};

const useInitialData = <T extends BaseOption>(
  ids: string[],
  fetchFunction: (ids: string[]) => Promise<T[]>
) => {
  const [options, setOptions] = useState<T[]>([]);
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    // Only fetch once - check if we've already fetched
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true; // Mark as fetched
      if (ids.length === 0) {
        setOptions([]);
        return;
      }
      const fetchData = async () => {
        try {
          const data = await fetchFunction(ids);
          setOptions(data);
        } catch (err) {
          setOptions([]);
        }
      };

      fetchData();
    }
  }, [ids, fetchFunction]);

  return { options };
};

export const useApPayeeInitialData = (payeeIds: string[]) => {
  const { fetchApPayees } = useGetApPayeesData();
  const { options } = useInitialData(payeeIds, fetchApPayees);

  return options;
};

export const useOfficeMembersInitialData = (memberIds: string[]) => {
  const { fetchOfficeMembers } = useGetOfficeMembersData();
  const { options } = useInitialData(memberIds, fetchOfficeMembers);

  return options;
};

export const useDeptInitialData = (deptIds: string[]) => {
  const { fetchDepts } = useGetDeptsData();
  const { options } = useInitialData(deptIds, fetchDepts);

  return options;
};
